/**
 * desktopContent
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared helper for capping single-column screen content on desktop builds
 * (web + Electron).
 *
 * The desktop widget body is ~1272px wide (DesktopLayout's WIDGET_MAX_WIDTH
 * minus gutters). Stretching every screen's content to fill that width turns
 * what should be a focused settings list / form / onboarding step into a
 * dashboard row — uncomfortable line lengths, oversized tap targets, and
 * visual weight that dwarfs the content. Phones are unaffected because their
 * viewport is narrower than the cap.
 *
 * Usage (inside a screen):
 *   const desktopWidth = useDesktopContentMaxWidth();      // default 720
 *   const desktopWidth = useDesktopContentMaxWidth(520);   // narrower (forms)
 *
 *   <ScrollView contentContainerStyle={[styles.scroll, desktopWidth]}>
 *     ...
 *   </ScrollView>
 *
 *   // or wrap inner content:
 *   <View style={desktopWidth}>...</View>
 *
 * Returns `null` on mobile / narrow viewports so the helper is a no-op when
 * unused — drop-in safe for any screen.
 */

import { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { isWeb, DESKTOP_BREAKPOINT } from './platform';

/** Default max content width: comfortable for settings lists & long-form copy. */
export const DESKTOP_CONTENT_MAX_WIDTH = 720;

/** Narrower max content width: focused forms, onboarding steps, action lists. */
export const DESKTOP_CONTENT_NARROW_WIDTH = 520;

interface DesktopContentStyle {
  width: '100%';
  maxWidth: number;
  alignSelf: 'center';
}

/**
 * Returns a style object that caps content width on desktop and centers it
 * horizontally, or `null` on mobile/narrow viewports. Spread or pass directly
 * to a `style` / `contentContainerStyle` array.
 */
export function useDesktopContentMaxWidth(
  maxWidth: number = DESKTOP_CONTENT_MAX_WIDTH,
): DesktopContentStyle | null {
  const { width } = useWindowDimensions();
  return useMemo(() => {
    if (!isWeb) return null;
    if (width < DESKTOP_BREAKPOINT) return null;
    return {
      width: '100%',
      maxWidth,
      alignSelf: 'center',
    };
  }, [width, maxWidth]);
}
